/* eslint-disable react-refresh/only-export-components */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useRequest from '../hooks/useRequest';

interface CashDrawerContextType {
    balance: number | null;
    cashPointId: number | null;
    setCashPointId: (id: number | null) => void;
    refreshBalance: () => Promise<void>;
    isLoading: boolean;
}

const CashDrawerContext = createContext<CashDrawerContextType | undefined>(undefined);

interface CashDrawerProviderProps {
    children: React.ReactNode;
}

export const CashDrawerProvider = ({ children }: CashDrawerProviderProps) => {
    const request = useRequest();
    const [balance, setBalance] = useState<number | null>(null);
    const [cashPointId, setCashPointId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    // Use a ref to track the current cashPointId for the interval callback
    const cashPointIdRef = useRef<number | null>(null);

    // Keep ref in sync with state
    useEffect(() => {
        cashPointIdRef.current = cashPointId;
    }, [cashPointId]);

    const refreshBalance = useCallback(async () => {
        // Use ref to get the current cashPointId value (avoids stale closure in interval)
        const currentCashPointId = cashPointIdRef.current;
        if (currentCashPointId === null) {
            // No cashpoint set yet, cannot fetch balance
            return;
        }
        try {
            setIsLoading(true);
            const response = await request<{ balance: number }>({
                path: `/cash-drawer/current-balance/${currentCashPointId}`,
            });
            setBalance(response.balance);
        } catch (error) {
            console.error('Failed to fetch cash drawer balance:', error);
            setBalance(null);
        } finally {
            setIsLoading(false);
        }
    }, [request]);

    // Refresh balance when cashPointId changes
    useEffect(() => {
        if (cashPointId !== null) {
            refreshBalance();
        }
    }, [cashPointId, refreshBalance]);

    useEffect(() => {
        // Refresh balance every 30 seconds as a fallback
        const interval = setInterval(refreshBalance, 30000);
        return () => clearInterval(interval);
    }, [refreshBalance]);

    const contextValue = useMemo(
        () => ({ balance, cashPointId, setCashPointId, refreshBalance, isLoading }),
        [balance, cashPointId, refreshBalance, isLoading],
    );

    return <CashDrawerContext.Provider value={contextValue}>{children}</CashDrawerContext.Provider>;
};

export const useCashDrawer = (): CashDrawerContextType => {
    const context = useContext(CashDrawerContext);
    if (context === undefined) {
        throw new Error('useCashDrawer must be used within a CashDrawerProvider');
    }
    return context;
};

export default CashDrawerContext;
